import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ShoppingCart,
  Trash2,
  ArrowLeft,
  Plus,
  Minus,
  Package,
  FileText,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useDraftInvoiceStore } from '@/stores/draftInvoiceStore';
import { useAuthStore } from '@/stores/authStore';
import { useClients } from '@/hooks/queries/useClients';
import { useCreateInvoice, useUpdateInvoice, useNextInvoiceNumber, useInvoice } from '@/hooks/queries/useInvoices';
import { Combobox } from '@/components/ui/combobox';
import { DatePicker } from '@/components/ui/date-picker';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/toast/useToast';

export const Route = createFileRoute('/draft-invoice/')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  validateSearch: (search: Record<string, unknown>) => ({
    id: (search.id as string) || undefined,
  }),
  component: DraftInvoicePage,
});

function DraftInvoicePage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const editingId = search?.id;
  
  const { company } = useAuthStore();
  const { items, updateQuantity, removeItem, clearCart } = useDraftInvoiceStore();
  const { data: clients = [] } = useClients(company?.id || '');
  const { data: nextNumber } = useNextInvoiceNumber(company?.id || '', 'invoice');
  const { data: existingInvoice } = useInvoice(editingId || '');
  const createInvoice = useCreateInvoice();
  const updateInvoice = useUpdateInvoice();

  const [selectedClient, setSelectedClient] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date());
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [notes, setNotes] = useState('');

  // Initialize invoice number when nextNumber changes
  useEffect(() => {
    if (nextNumber && !invoiceNumber && !editingId) {
      setInvoiceNumber(nextNumber);
    }
  }, [nextNumber, invoiceNumber, editingId]);

  // Load existing invoice data
  useEffect(() => {
    if (existingInvoice) {
      setSelectedClient(existingInvoice.client_id);
      setInvoiceDate(new Date(existingInvoice.created_at));
      setInvoiceNumber(existingInvoice.invoice_number);
      setNotes(existingInvoice.notes || '');
    }
  }, [existingInvoice]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const calculateLineTotal = (item: typeof items[0]) => {
    const subtotal = item.quantity * item.unitPrice;
    const vatAmount = subtotal * (item.vat / 100);
    return subtotal + vatAmount;
  };

  const calculations = {
    subtotal: items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0),
    vatAmount: items.reduce((sum, item) => {
      const subtotal = item.quantity * item.unitPrice;
      return sum + subtotal * (item.vat / 100);
    }, 0),
    get fiscalStamp() {
      return this.subtotal > 5000 ? 100 : 0;
    },
    get total() {
      return this.subtotal + this.vatAmount + this.fiscalStamp;
    },
  };

  const handleUpdateQuantity = (productId: string, delta: number) => {
    const item = items.find((i) => i.productId === productId);
    if (item) {
      const newQuantity = Math.max(1, item.quantity + delta);
      updateQuantity(productId, newQuantity);
    }
  };

  const incrementInvoiceNumber = (invNumber: string): string => {
    // Extract the number part and increment
    const match = invNumber.match(/^(.+?)(\d+)$/);
    if (match) {
      const prefix = match[1];
      const num = parseInt(match[2]) + 1;
      const paddedNum = num.toString().padStart(match[2].length, '0');
      return `${prefix}${paddedNum}`;
    }
    // If no number found, append -1
    return `${invNumber}-1`;
  };

  const handleCreateInvoice = async () => {
    if (!selectedClient) {
      toast.error(
        'خطأ / Erreur', 
        'يرجى اختيار عميل\nVeuillez sélectionner un client'
      );
      return;
    }

    if (items.length === 0) {
      toast.error(
        'خطأ / Erreur', 
        'يرجى إضافة منتجات\nVeuillez ajouter des produits'
      );
      return;
    }

    if (!invoiceNumber || invoiceNumber.trim() === '') {
      toast.error(
        'خطأ / Erreur',
        'يرجى إدخال رقم الفاتورة\nVeuillez entrer le numéro de facture'
      );
      return;
    }

    try {
      const invoiceItems = items.map((item) => ({
        product_id: item.productId,
        description: item.productName,
        quantity: item.quantity,
        unit: item.unit,
        unit_price: item.unitPrice,
        discount: 0,
        discount_type: 'percentage' as const,
        vat: item.vat,
        total: calculateLineTotal(item),
      }));

      const invoiceData = {
        type: 'invoice' as const,
        invoice_number: invoiceNumber.trim(),
        client_id: selectedClient,
        company_id: company!.id,
        date: invoiceDate.toISOString(),
        due_date: null,
        status: 'draft' as const,
        subtotal: calculations.subtotal,
        vat_amount: calculations.vatAmount,
        fiscal_stamp: calculations.fiscalStamp,
        discount: 0,
        total: calculations.total,
        paid: 0,
        balance: calculations.total,
        notes: notes || null,
        terms_and_conditions: null,
        language: 'fr' as const,
        sync_status: 'synced' as const,
      };

      if (editingId) {
        // Update existing invoice
        await updateInvoice.mutateAsync({
          id: editingId,
          ...invoiceData,
          items: invoiceItems,
        });
        toast.success('تم التحديث / Modifiée', 'تم تحديث الفاتورة بنجاح\nFacture modifiée avec succès');
      } else {
        // Create new invoice
        await createInvoice.mutateAsync({
          ...invoiceData,
          items: invoiceItems,
        });
        toast.success('تم الإنشاء / Créée', 'تم إنشاء الفاتورة بنجاح\nFacture créée avec succès');
      }

      clearCart();
      navigate({ to: '/apps/sales' });
    } catch (error: any) {
      console.error('Error creating invoice:', error);
      
      // Handle duplicate invoice number error
      if (error?.message?.includes('duplicate key') || error?.code === '23505') {
        const newNumber = incrementInvoiceNumber(invoiceNumber);
        setInvoiceNumber(newNumber);
        
        toast.error(
          'رقم مكرر / Numéro en double',
          `رقم الفاتورة "${invoiceNumber}" مستخدم بالفعل. تم التحديث إلى "${newNumber}"\n\nLe numéro "${invoiceNumber}" existe déjà. Mis à jour vers "${newNumber}"\n\nVeuillez réessayer.`
        );
        return;
      }

      // Handle stock errors
      if (error?.message?.includes('Stock insuffisant')) {
        toast.error('مخزون غير كاف / Stock insuffisant', error.message);
        return;
      }

      // Generic error
      toast.error(
        'خطأ / Erreur', 
        `فشل في إنشاء الفاتورة\nÉchec de création de la facture\n\n${error?.message || 'Erreur inconnue'}`
      );
    }
  };

  if (items.length === 0) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <Button variant="outline" size="sm" onClick={() => navigate({ to: '/apps/products' })}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              العودة / Retour
            </Button>
          </div>

          <div className="flex flex-col items-center justify-center py-20">
            <ShoppingCart className="w-20 h-20 text-gray-300 mb-6" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              الفاتورة فارغة / Panier vide
            </h2>
            <p className="text-gray-600 mb-6 text-center">
              لم تقم بإضافة أي منتجات بعد / Vous n'avez pas encore ajouté de produits
            </p>
            <Button
              onClick={() => navigate({ to: '/apps/products' })}
              className="bg-gradient-to-r from-emerald-500 to-teal-600"
            >
              <Package className="w-4 h-4 mr-2" />
              تصفح المنتجات / Parcourir les produits
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              فاتورة مسودة / Facture brouillon
            </h1>
            <p className="mt-2 text-gray-600">
              أكمل تفاصيل الفاتورة / Complétez les détails de la facture
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate({ to: '/apps/products' })}>
            <Plus className="w-4 h-4 mr-2" />
            إضافة منتجات / Ajouter produits
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Invoice Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Client Selection */}
            <Card>
              <CardHeader>
                <CardTitle>معلومات العميل / Informations client</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>العميل / Client *</Label>
                  <Combobox
                    items={clients}
                    value={selectedClient}
                    onValueChange={setSelectedClient}
                    getLabel={(client) => client.name}
                    getValue={(client) => client.id}
                    getDescription={(client) => `${client.phone} - ${client.wilaya}`}
                    placeholder="اختر العميل / Sélectionner un client"
                    searchPlaceholder="بحث... / Rechercher..."
                    emptyText="لا يوجد عملاء / Aucun client"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>التاريخ / Date</Label>
                    <DatePicker value={invoiceDate} onChange={(date) => setInvoiceDate(date || new Date())} />
                  </div>
                  <div className="space-y-2">
                    <Label>الرقم / Numéro *</Label>
                    <Input 
                      value={invoiceNumber} 
                      onChange={(e) => setInvoiceNumber(e.target.value)}
                      placeholder="Ex: 2025-005"
                      className="font-mono"
                    />
                    <p className="text-xs text-gray-500">
                      Modifiable - Le numéro doit être unique
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>ملاحظات / Notes</Label>
                  <Input
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="ملاحظات اختيارية... / Notes optionnelles..."
                  />
                </div>
              </CardContent>
            </Card>

            {/* Items */}
            <Card>
              <CardHeader>
                <CardTitle>المنتجات / Produits ({items.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {items.map((item) => (
                    <div
                      key={item.productId}
                      className="flex items-center gap-4 p-4 border rounded-lg hover:border-emerald-200 transition-colors"
                    >
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{item.productName}</h4>
                        <p className="text-sm text-gray-500">
                          Code: {item.productCode} | TVA: {item.vat}%
                        </p>
                        <p className="text-sm font-medium text-gray-700 mt-1">
                          {formatCurrency(item.unitPrice)} x {item.quantity} {item.unit}
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleUpdateQuantity(item.productId, -1)}
                          disabled={item.quantity <= 1}
                        >
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className="w-12 text-center font-medium">{item.quantity}</span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleUpdateQuantity(item.productId, 1)}
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                      </div>

                      <div className="text-right min-w-[100px]">
                        <p className="font-semibold text-gray-900">
                          {formatCurrency(calculateLineTotal(item))}
                        </p>
                      </div>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(item.productId)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-20">
              <CardHeader>
                <CardTitle>الملخص / Résumé</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">الإجمالي الفرعي / Sous-total:</span>
                    <span className="font-medium">{formatCurrency(calculations.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">TVA:</span>
                    <span className="font-medium">{formatCurrency(calculations.vatAmount)}</span>
                  </div>
                  {calculations.fiscalStamp > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">الطابع الجبائي / Timbre:</span>
                      <span className="font-medium">{formatCurrency(calculations.fiscalStamp)}</span>
                    </div>
                  )}
                  <div className="border-t pt-2 flex justify-between">
                    <span className="font-bold text-lg">المجموع / TOTAL:</span>
                    <span className="font-bold text-lg text-emerald-600">
                      {formatCurrency(calculations.total)}
                    </span>
                  </div>
                </div>

                <div className="space-y-2 pt-4 border-t">
                  <Button
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-600"
                    size="lg"
                    onClick={handleCreateInvoice}
                    disabled={!selectedClient || !invoiceNumber || createInvoice.isPending}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    إنشاء الفاتورة / Créer la facture
                  </Button>

                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      if (confirm('هل أنت متأكد؟ / Êtes-vous sûr ?')) {
                        clearCart();
                        navigate({ to: '/apps/products' });
                      }
                    }}
                  >
                    إلغاء / Annuler
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
